import { GridLegacy as Grid, styled } from '@mui/material'
import { ConfirmationDialog, FormFooter, HeadlineWithSpacing } from 'ComponentLibrary'
import { HeadlineWithSpacingProps } from 'ComponentLibrary/src/molecules/HeadlineWithSpacing/HeadlineWithSpacing'
import { contentSpacing, denseContentSpacing } from 'ComponentLibrary/src/styles/moduleSpacings'
import { ReactNode, useState } from 'react'

export type FormTemplateProps = {
    children: React.ReactNode
    onSubmit?: () => void
    onCancel?: () => void
    hideFooter?: boolean
    isFormDirty?: boolean
    hasErrorOnMutate?: boolean
    hideFooterOnClean?: boolean
    shouldDisableSubmit?: boolean
    headline?: HeadlineWithSpacingProps
    className?: string
    formAriaLabel?: string
    as?: 'form' | 'div'
    dense?: boolean
    columns?: number
    useConfirmationDialog?: boolean
    isPersisting?: boolean
    denseFooter?: boolean
    formFooterStyles?: React.CSSProperties
    additionalFooterElement?: ReactNode
    confirmationDialogTitle?: string
    confirmationDialogConfirmLabel?: string
    confirmationDialogCancelLabel?: string
}

export default function FormTemplate({
    children,
    onSubmit,
    onCancel,
    hideFooter = false,
    headline,
    className,
    columns,
    formAriaLabel,
    isFormDirty,
    isPersisting,
    hideFooterOnClean = false,
    shouldDisableSubmit = false,
    dense = false,
    denseFooter = false,
    useConfirmationDialog = true,
    formFooterStyles,
    additionalFooterElement,
    confirmationDialogTitle = 'Alle ungespeicherten Änderungen verwerfen?',
    confirmationDialogConfirmLabel = 'Verwerfen',
    confirmationDialogCancelLabel = 'Abbrechen',
}: FormTemplateProps) {
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
    const handleCancel = () => {
        if (isFormDirty && useConfirmationDialog) {
            setShowConfirmationDialog(true)
        } else {
            onCancel?.()
        }
    }

    const onCancelConfirmation = () => {
        setShowConfirmationDialog(false)
    }

    const onConfirm = () => {
        setShowConfirmationDialog(false)
        onCancel?.() // This is confusing, but onCancel is actually the reset function
    }

    const shouldHideFooter = hideFooter || (hideFooterOnClean && !isFormDirty)

    return (
        <>
            {headline && (
                <HeadlineWithSpacing
                    title={headline.title}
                    subtitle={headline.subtitle}
                    locked={headline.locked}
                    setLocked={headline.setLocked}
                    onClickAddItem={headline.onClickAddItem}
                    options={headline.options}
                    isPersisting={headline.isPersisting || isPersisting}
                    variant={headline.variant ? headline.variant : 'headline2'}
                    optionsIcon={headline.optionsIcon}
                />
            )}
            <FormContainer className={className} aria-label={formAriaLabel}>
                <Grid
                    container
                    columns={columns}
                    width={`calc(100% + ${!dense ? contentSpacing : denseContentSpacing}px)`}
                    spacing={`${!dense ? contentSpacing : denseContentSpacing}px`}
                    sx={{ marginLeft: `-${!dense ? contentSpacing : denseContentSpacing}px` }}
                >
                    {children}
                </Grid>
                <Grid style={formFooterStyles}>
                    {!shouldHideFooter && (
                        <FormFooter
                            onSubmit={onSubmit}
                            shouldDisableSubmit={shouldDisableSubmit}
                            onCancel={onCancel && handleCancel}
                            isPersisting={isPersisting}
                            dense={denseFooter}
                            reducedPadding={dense}
                            additionalFooterElement={additionalFooterElement}
                        />
                    )}
                </Grid>
                <ConfirmationDialog
                    title={confirmationDialogTitle}
                    open={showConfirmationDialog}
                    onCancel={onCancelConfirmation}
                    onConfirm={onConfirm}
                    destructive
                    confirmLabel={confirmationDialogConfirmLabel}
                    cancelLabel={confirmationDialogCancelLabel}
                />
            </FormContainer>
        </>
    )
}

const FormContainer = styled('form')(() => ({
    width: '100%',
    //Cases where a <form> immediately follows another <form> with no headlines in between
    '& + &': {
        marginTop: `${contentSpacing}px`,
    },
}))
